/**
 * Bulk price check.
 *
 * Runs AI price analysis for every visible lot that still needs pricing
 * (no estimates, analysis not already pending, not closed/deleted). Shows a
 * confirm dialog with the lot + credit count first, then executes with small
 * concurrency through the same per-lot retry flow the Price Check button
 * uses, reusing its spinner state. Stops immediately on insufficient credits.
 *
 * @module portal/modules/bulkPriceCheck
 */

import type { Lot, PortalUser, State } from '../types';
import { hasPricingData } from './rendering';
import { isLotClosed } from './lotOperations';
import { syncPriceReviewRemote } from './priceReview';
import { startPriceReviewSpinner, clearPriceReviewSpinner } from './priceReview';
import { clearCreditCache } from './credit';
import { showToast } from './ui';
import { createLogger } from '../../shared/logger.js';

const log = createLogger('portal:bulkPriceCheck');

const CONCURRENCY = 2;

let bulkRunning = false;

export function isBulkPriceCheckRunning(): boolean {
  return bulkRunning;
}

/** Lots eligible for a bulk price check (mirrors the "Needs Price" filter). */
export function findPriceCheckCandidates(state: State, lots: Lot[]): Lot[] {
  if (state.mode !== 'auth0') return [];
  return lots.filter(lot => {
    if (!lot || !lot.id || lot.deleted) return false;
    if (!lot.remote) return false;
    if (hasPricingData(lot)) return false;
    if (lot.aiStatus === 'processing' || lot.aiStatus === 'pending') return false;
    if (state.pendingPriceReviewIds?.has(lot.id)) return false;
    if (isLotClosed(lot)) return false;
    return true;
  });
}

export interface BulkPriceCheckResult {
  requested: number;
  succeeded: number;
  failed: number;
  stoppedForCredits: boolean;
}

export interface BulkPriceCheckDeps {
  state: State;
  render: () => void;
  ensureAccessToken: (retry?: boolean) => Promise<string | null>;
  mapRemoteDocument: (data: Record<string, unknown>) => Lot | null | undefined;
  applyRemoteSnapshot: (lots: Lot[]) => void;
  openCheckoutModal?: () => void;
}

/**
 * Run the bulk price check over the given candidates.
 * Caller is responsible for the confirm step.
 */
export async function runBulkPriceCheck(
  candidates: Lot[],
  deps: BulkPriceCheckDeps
): Promise<BulkPriceCheckResult> {
  const { state, render, ensureAccessToken, mapRemoteDocument, applyRemoteSnapshot } = deps;
  const result: BulkPriceCheckResult = {
    requested: candidates.length,
    succeeded: 0,
    failed: 0,
    stoppedForCredits: false,
  };
  const user = (state.user ?? {}) as PortalUser;
  if (!candidates.length || bulkRunning) return result;

  bulkRunning = true;
  let stop = false;
  const queue = [...candidates];

  const worker = async (): Promise<void> => {
    while (!stop) {
      const lot = queue.shift();
      if (!lot) return;

      if (!state.pendingPriceReviewIds) state.pendingPriceReviewIds = new Set();
      state.pendingPriceReviewIds.add(lot.id);
      startPriceReviewSpinner(lot.id, state, render);
      lot.priceReview = Object.assign({}, lot.priceReview || {}, { status: 'failedPrice' });
      lot.aiStatus = 'processing';

      try {
        const updated = await syncPriceReviewRemote(
          lot,
          'failedPrice',
          user,
          false,
          ensureAccessToken,
          mapRemoteDocument,
          false
        );
        if (updated) {
          const index = state.lots.findIndex(item => item && item.id === updated.id);
          if (index >= 0) state.lots[index] = updated;
          applyRemoteSnapshot(state.lots);
        }
        result.succeeded += 1;
        clearCreditCache();
      } catch (err) {
        result.failed += 1;
        clearPriceReviewSpinner(lot.id, state, render);
        if ((err as { status?: number })?.status === 402) {
          // No credits left — stop the whole batch.
          result.stoppedForCredits = true;
          stop = true;
        } else {
          log.warn('Bulk price check failed for lot', lot.id, err);
        }
      } finally {
        state.pendingPriceReviewIds?.delete(lot.id);
      }
    }
  };

  try {
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
  } finally {
    bulkRunning = false;
    render();
  }
  return result;
}

/**
 * Toolbar entry point: confirm, run, and summarize.
 */
export async function handleBulkPriceCheck(deps: BulkPriceCheckDeps): Promise<void> {
  const { state, openCheckoutModal } = deps;
  if (bulkRunning) {
    showToast('A bulk price check is already running.', { variant: 'info', duration: 2500 });
    return;
  }
  if (state.mode !== 'auth0') {
    showToast('Sign in to run AI price checks.', { variant: 'warning', duration: 3000 });
    return;
  }

  const candidates = findPriceCheckCandidates(state, state.lots || []);
  if (!candidates.length) {
    showToast('No lots need a price check right now.', { variant: 'info', duration: 3000 });
    return;
  }

  const credits = state.creditBalance?.creditsRemaining;
  const count = candidates.length;
  const creditNote =
    typeof credits === 'number' ? ` You have ${credits} credit${credits === 1 ? '' : 's'}.` : '';
  const confirmed = window.confirm(
    `Run AI price analysis for ${count} lot${count === 1 ? '' : 's'}? ` +
      `This uses ${count} credit${count === 1 ? '' : 's'} (1 per lot; failures are refunded).${creditNote}`
  );
  if (!confirmed) return;

  showToast(`Price-checking ${count} lot${count === 1 ? '' : 's'}…`, {
    variant: 'info',
    duration: 4000,
  });

  const result = await runBulkPriceCheck(candidates, deps);

  if (result.stoppedForCredits) {
    showToast(
      `Ran out of credits after ${result.succeeded} lot${result.succeeded === 1 ? '' : 's'}.`,
      { variant: 'error', duration: 5000 }
    );
    if (openCheckoutModal && window.confirm('Out of credits. Purchase more to continue?')) {
      openCheckoutModal();
    }
    return;
  }

  const failuresNote = result.failed ? ` (${result.failed} failed — credits refunded)` : '';
  showToast(
    `Requested analysis for ${result.succeeded} lot${result.succeeded === 1 ? '' : 's'}${failuresNote}.`,
    { variant: result.failed ? 'warning' : 'success', duration: 5000 }
  );
}
